'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MicroScenarioBody, MicroScenarioOutcome } from '@akademi/site-builder-runtime';
import type { RendererProps } from '../types';
import { ErrorState, PopupHeader, padded } from '../chrome';

const OUTCOME_PALETTE: Record<MicroScenarioOutcome, { fg: string; bg: string; border: string }> = {
  success: {
    fg: 'var(--color-success-500, #16a34a)',
    bg: 'rgba(22,163,74,0.08)',
    border: 'rgba(22,163,74,0.3)',
  },
  failure: {
    fg: 'var(--color-danger, #dc2626)',
    bg: 'rgba(220,38,38,0.08)',
    border: 'rgba(220,38,38,0.3)',
  },
  neutral: {
    fg: 'var(--color-text-secondary, #71717a)',
    bg: 'var(--color-surface-page, #f4f4f5)',
    border: 'var(--color-border, rgba(0,0,0,0.12))',
  },
};

/**
 * Branching scenario walker over the canonical {@code MicroScenarioBody}
 * the admin editor writes. Starts at {@code startNodeId} (the admin
 * pre-selects {@code nodes[0]} without writing it, so that's the
 * fallback); choices advance via {@code nextNodeId}; a node with
 * {@code isTerminal} ends the scenario, fires {@code onComplete} once,
 * and is styled by its {@code outcome}. {@code intro} renders above the
 * start node until the first choice is taken.
 */
export function ScenarioRenderer({ item, onComplete }: RendererProps) {
  const { t } = useTranslation('site-builder-blocks');
  const body = (item.body ?? {}) as MicroScenarioBody;
  const nodes = body.nodes ?? [];
  const startId = body.startNodeId ?? nodes[0]?.id;
  const [currentId, setCurrentId] = useState(startId);
  const [history, setHistory] = useState<string[]>([]);
  const completeFiredRef = useRef(false);

  const node = nodes.find((n) => n.id === currentId);
  const isTerminal = node?.isTerminal === true;

  // Terminal reached = scenario complete. Guarded — backing out of the
  // ending and reaching another must not double-fire.
  useEffect(() => {
    if (isTerminal && !completeFiredRef.current) {
      completeFiredRef.current = true;
      onComplete();
    }
  }, [isTerminal, onComplete]);

  if (!node) {
    return (
      <>
        <PopupHeader kind={item.kind} title={item.title} description={item.description} />
        <ErrorState message={t('microScenario.invalid')} />
      </>
    );
  }

  const outcome = OUTCOME_PALETTE[node.outcome ?? 'neutral'];

  return (
    <>
      <PopupHeader
        kind={item.kind}
        title={item.title}
        meta={
          <div
            style={{
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--color-text-tertiary, #94867d)',
            }}
          >
            {t('microScenario.step', { step: history.length + 1 })}
          </div>
        }
      />
      <div style={{ ...padded, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {body.intro && history.length === 0 && node.id === startId && (
          <div
            style={{
              padding: 'var(--space-3, 12px) var(--space-4, 16px)',
              borderRadius: 8,
              background: 'var(--color-surface-page, #f8fafc)',
              fontSize: 'var(--font-size-sm, 14px)',
              lineHeight: 1.6,
              color: 'var(--color-text-secondary, #71717a)',
            }}
          >
            {body.intro}
          </div>
        )}
        {node.imageUrl && (
          <img
            src={node.imageUrl}
            alt=""
            style={{ width: '100%', borderRadius: 8, objectFit: 'cover' }}
          />
        )}
        {isTerminal ? (
          <div
            style={{
              padding: 'var(--space-4, 16px) var(--space-5, 20px)',
              borderRadius: 12,
              background: outcome.bg,
              border: `1px solid ${outcome.border}`,
            }}
          >
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: 700,
                letterSpacing: 'var(--font-tracking-wider, 0.04em)',
                textTransform: 'uppercase',
                color: outcome.fg,
                marginBottom: 8,
              }}
            >
              {t(`microScenario.outcome.${node.outcome ?? 'neutral'}`)}
            </div>
            <div style={{ fontSize: 'var(--font-size-md, 16px)', lineHeight: 1.6 }}>
              {node.text}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 'var(--font-size-md, 16px)', lineHeight: 1.6 }}>{node.text}</div>
        )}
        {!isTerminal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(node.choices ?? []).map((c, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  if (!nodes.some((n) => n.id === c.nextNodeId)) return;
                  setHistory((h) => [...h, node.id]);
                  setCurrentId(c.nextNodeId);
                }}
                style={{
                  textAlign: 'left',
                  padding: 'var(--space-3, 12px) var(--space-4, 16px)',
                  border: '1px solid var(--color-border, rgba(0,0,0,0.12))',
                  borderRadius: 8,
                  background: 'var(--color-surface-card, #fff)',
                  fontSize: 'var(--font-size-sm, 14px)',
                  cursor: 'pointer',
                }}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}
        {history.length > 0 && (
          <button
            type="button"
            onClick={() => {
              const prev = history[history.length - 1];
              if (prev) {
                setCurrentId(prev);
                setHistory((h) => h.slice(0, -1));
              }
            }}
            style={{
              alignSelf: 'flex-start',
              fontSize: 'var(--font-size-xs, 12px)',
              background: 'none',
              border: 'none',
              color: 'var(--color-text-secondary, #71717a)',
              cursor: 'pointer',
            }}
          >
            {t('microScenario.back')}
          </button>
        )}
      </div>
    </>
  );
}
